import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { MIN_ZOOM, MAX_ZOOM, VIRTUAL_SIZE, bboxOf } from '../utils/canvasUtils';

// Хук для управления навигацией и зумом
export const useNavigation = (containerRef) => {
    const [zoom, setZoom] = useState(1);
    const [spacePressed, setSpacePressed] = useState(false);
    const [viewportCenter, setViewportCenter] = useState({ x: 0, y: 0 });

    // Ref для хранения целевой позиции скролла (для fitBBoxInView)
    const pendingScrollRef = useRef(null);

    const panStateRef = useRef({
        panning: false,
        startX: 0,
        startY: 0,
        startScrollLeft: 0,
        startScrollTop: 0
    });

    // Зум с сохранением центра видимой области
    const zoomIn = () => {
        const c = containerRef.current;
        if (!c) return;

        const oldZoom = zoom;
        const newZoom = Math.min(MAX_ZOOM, oldZoom * 1.2);
        if (newZoom === oldZoom) return;

        const centerX = (c.scrollLeft + c.clientWidth / 2) / oldZoom;
        const centerY = (c.scrollTop + c.clientHeight / 2) / oldZoom;

        setZoom(newZoom);

        requestAnimationFrame(() => {
            const newScrollLeft = centerX * newZoom - c.clientWidth / 2;
            const newScrollTop = centerY * newZoom - c.clientHeight / 2;
            c.scrollLeft = Math.min(
                Math.max(0, newScrollLeft),
                VIRTUAL_SIZE * newZoom - c.clientWidth
            );
            c.scrollTop = Math.min(
                Math.max(0, newScrollTop),
                VIRTUAL_SIZE * newZoom - c.clientHeight
            );
        });
    };

    const zoomOut = () => {
        const c = containerRef.current;
        if (!c) return;

        const oldZoom = zoom;
        const newZoom = Math.max(MIN_ZOOM, oldZoom / 1.2);
        if (newZoom === oldZoom) return;

        const centerX = (c.scrollLeft + c.clientWidth / 2) / oldZoom;
        const centerY = (c.scrollTop + c.clientHeight / 2) / oldZoom;

        setZoom(newZoom);

        requestAnimationFrame(() => {
            const newScrollLeft = centerX * newZoom - c.clientWidth / 2;
            const newScrollTop = centerY * newZoom - c.clientHeight / 2;
            c.scrollLeft = Math.min(
                Math.max(0, newScrollLeft),
                VIRTUAL_SIZE * newZoom - c.clientWidth
            );
            c.scrollTop = Math.min(
                Math.max(0, newScrollTop),
                VIRTUAL_SIZE * newZoom - c.clientHeight
            );
        });
    };

    const fitToBoard = () => {
        const c = containerRef.current;
        if (!c) return;
        const scale = Math.min(
            c.clientWidth / VIRTUAL_SIZE,
            c.clientHeight / VIRTUAL_SIZE
        );
        const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale));
        setZoom(newZoom);
        setTimeout(() => {
            c.scrollLeft = (VIRTUAL_SIZE * newZoom - c.clientWidth) / 2;
            c.scrollTop = (VIRTUAL_SIZE * newZoom - c.clientHeight) / 2;
        }, 0);
    };

    const fitBBoxInView = useCallback((bbox) => {
        const c = containerRef.current;
        if (!c || !bbox) return;
        const pad = 80;
        const w = Math.max(1, bbox.maxX - bbox.minX);
        const h = Math.max(1, bbox.maxY - bbox.minY);
        const zX = (c.clientWidth - pad * 2) / w;
        const zY = (c.clientHeight - pad * 2) / h;
        let newZoom = Math.min(zX, zY);
        if (!isFinite(newZoom) || newZoom <= 0) newZoom = zoom;
        newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

        // Вычисляем целевой скролл до setZoom
        const cx = (bbox.minX + bbox.maxX) / 2;
        const cy = (bbox.minY + bbox.maxY) / 2;
        const targetScrollLeft = cx * newZoom - c.clientWidth / 2;
        const targetScrollTop = cy * newZoom - c.clientHeight / 2;

        // Сохраняем целевой скролл в ref для применения после рендера
        pendingScrollRef.current = {
            left: Math.max(0, targetScrollLeft),
            top: Math.max(0, targetScrollTop)
        };

        setZoom(newZoom);
    }, [containerRef, zoom]);

    const centerToSelectionOrDiagram = (elements, selectedElementIds, selectedElementId) => {
        const ids = selectedElementIds.size
            ? Array.from(selectedElementIds)
            : selectedElementId
                ? [selectedElementId]
                : [];
        const targetEls = ids.length
            ? elements.filter((e) => ids.includes(e.id))
            : elements;
        const bb = bboxOf(targetEls);
        if (bb) fitBBoxInView(bb);
    };

    const startPan = (e) => {
        const c = containerRef.current;
        if (!c) return;
        panStateRef.current = {
            panning: true,
            startX: e.clientX,
            startY: e.clientY,
            startScrollLeft: c.scrollLeft,
            startScrollTop: c.scrollTop
        };

        const onMove = (ev) => {
            if (!panStateRef.current.panning) return;
            c.scrollLeft = panStateRef.current.startScrollLeft - (ev.clientX - panStateRef.current.startX);
            c.scrollTop = panStateRef.current.startScrollTop - (ev.clientY - panStateRef.current.startY);
        };

        const onUp = () => {
            panStateRef.current.panning = false;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const handleWheel = (e) => {
        const c = containerRef.current;
        if (!c) return;
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const factor = 1.1;
            const dir = e.deltaY > 0 ? 1 : -1;
            const old = zoom;
            const nz = dir > 0
                ? Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, old / factor))
                : Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, old * factor));

            if (nz === old) return;

            const r = c.getBoundingClientRect();
            const mx = e.clientX - r.left;
            const my = e.clientY - r.top;
            const wx = (c.scrollLeft + mx) / old;
            const wy = (c.scrollTop + my) / old;
            setZoom(nz);

            requestAnimationFrame(() => {
                const tsl = wx * nz - mx;
                const tst = wy * nz - my;
                c.scrollLeft = Math.min(
                    Math.max(0, tsl),
                    VIRTUAL_SIZE * nz - c.clientWidth
                );
                c.scrollTop = Math.min(
                    Math.max(0, tst),
                    VIRTUAL_SIZE * nz - c.clientHeight
                );
            });
        }
    };

    // Центрируем канву при монтировании — один раз
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const c = containerRef.current;
        if (!c) return;
        const left = (VIRTUAL_SIZE * zoom - c.clientWidth) / 2;
        const top = (VIRTUAL_SIZE * zoom - c.clientHeight) / 2;
        c.scrollLeft = left;
        c.scrollTop = top;
        setViewportCenter({
            x: (left + c.clientWidth / 2) / zoom,
            y: (top + c.clientHeight / 2) / zoom
        });
    }, []);

    // Применяем отложенный скролл после изменения zoom (для fitBBoxInView)
    useLayoutEffect(() => {
        if (pendingScrollRef.current) {
            const c = containerRef.current;
            if (c) {
                c.scrollLeft = pendingScrollRef.current.left;
                c.scrollTop = pendingScrollRef.current.top;
            }
            pendingScrollRef.current = null;
        }
    }, [zoom, containerRef]);

    // Обновляем viewportCenter при скролле и resize без изменения scroll
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const c = containerRef.current;
        if (!c) return;

        const update = () =>
            setViewportCenter({
                x: (c.scrollLeft + c.clientWidth / 2) / zoom,
                y: (c.scrollTop + c.clientHeight / 2) / zoom
            });

        update();
        c.addEventListener('scroll', update);
        window.addEventListener('resize', update);

        return () => {
            c.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, [zoom]);

    return {
        zoom,
        setZoom, // добавлено: даём возможность внешне устанавливать зум (для загрузки сохранённого состояния)
        spacePressed,
        setSpacePressed,
        viewportCenter,
        zoomIn,
        zoomOut,
        fitToBoard,
        fitBBoxInView,
        centerToSelectionOrDiagram,
        startPan,
        handleWheel,
        panStateRef
    };
};
